import React, { useState } from 'react';
import axios from 'axios';
import Navbar from './DocNav';

const DiseaseDetection = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [result, setResult] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Handle file selection and preview
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setSelectedFile(file);
    setResult('');
    setError('');
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle form submission to upload the image
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      setError('Please select an image file.');
      return;
    }

    setIsLoading(true); // Start loading
    const formData = new FormData();
    formData.append('image', selectedFile);

    try {
      const response = await axios.post('http://localhost:5001/predict/images', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setResult(response.data.result);
      setError('');
    } catch (err) {
      console.error(err);
      setError('An error occurred while processing your request.');
      setResult('');
    } finally {
      setIsLoading(false); // Stop loading regardless of outcome
    }
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-200 py-28 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <h1 className="text-4xl font-extrabold text-gray-900 mb-2">Pneumonia Detection</h1>
            <p className="text-lg text-gray-600">Upload a chest X-ray image for analysis</p>
          </div>

          <div className="bg-white rounded-xl shadow-xl overflow-hidden">
            <form onSubmit={handleSubmit} className="p-8">
              <div className="mb-6">
                <label htmlFor="fileInput" className="block text-gray-700 text-sm font-medium mb-2">
                  Upload Chest X-Ray Image
                </label>
                <div className="relative border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-blue-500 transition-colors">
                  <input
                    type="file"
                    id="fileInput"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                  <div className="text-center">
                    <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48" aria-hidden="true">
                      <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <p className="mt-2 text-sm text-gray-600">
                      {selectedFile ? selectedFile.name : "Click or drag and drop to upload file"}
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      PNG, JPG, GIF up to 10MB
                    </p>
                  </div>
                </div>
              </div>

              {previewUrl && (
                <div className="mb-6">
                  <p className="block text-gray-700 text-sm font-medium mb-2">Image Preview:</p>
                  <div className="mt-2 relative rounded-lg overflow-hidden border border-gray-200">
                    <img src={previewUrl} alt="Preview" className="w-full h-auto object-contain max-h-64" />
                  </div>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className={`w-full flex items-center justify-center py-3 px-4 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all ${isLoading ? 'opacity-75 cursor-not-allowed' : 'transform hover:scale-[1.02]'}`}
              >
                {isLoading ? (
                  <>
                    {/* <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg> */}
                    Processing...
                  </>
                ) : 'Analyze Image'}
              </button>
            </form>
          </div>

          {isLoading && (
            <div className="mt-8 bg-white p-6 rounded-xl shadow-md text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mb-4"></div>
              <p className="text-gray-700">Analyzing your image. This may take a moment...</p>
            </div>
          )}

          {result && !isLoading && (
            <div className="mt-8 bg-white p-6 rounded-xl shadow-md border-l-4 border-green-500 animate-fade-in">
              <h2 className="text-xl font-bold text-gray-900 mb-2">Prediction Result</h2>
              <div className="bg-green-50 p-4 rounded-md">
                <p className="text-green-800 text-lg">{result}</p>
              </div>
            </div>
          )}

          {error && !isLoading && (
            <div className="mt-8 bg-white p-6 rounded-xl shadow-md border-l-4 border-red-500 animate-fade-in">
              <h2 className="text-xl font-bold text-gray-900 mb-2">Error</h2>
              <div className="bg-red-50 p-4 rounded-md">
                <p className="text-red-800">{error}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default DiseaseDetection;
